'use client';

import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { OverlayView } from '@react-google-maps/api';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
import { RootState } from '@/hotdesk-ui/store/store';
import styles from './MapContainer.module.scss';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { buildWorkspacePath } from '@/hotdesk-ui/helpers/workspace-url';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import CardOnMap from '../card-on-map/CardOnMap';
import { DistanceCalculatorService } from '@/hotdesk-ui/services/distance.calculator.service';
import ListingSliderPopupClient from '@/hotdesk-ui/components/listing-slider-popup/ListingSliderPopupClient';
import { getListingProviderCode } from '@/hotdesk-ui/utils/provider';
import { preferredPriceUnitFromFrequency } from '@/hotdesk-ui/helpers/explore-location-params';
import { useWorkspaceListings } from '@/hotdesk-ui/hooks/useWorkspaceListings';
import { useSearchParams } from 'next/navigation';
export interface Center {
  lat: number;
  lng: number;
}
const getObjectKeys = (obj: unknown): string[] => {
  return Object.keys(obj as object);
};
const checkArray = (arr: string[]): string => {
  if (arr?.includes('hour')) {
    return 'hour';
  } else if (arr?.includes('day')) {
    return 'day';
  } else if (arr?.includes('month')) {
    return 'month';
  } else {
    return 'annual';
  }
};
const resolvePriceUnit = (priceKeys: string[], frequency: string | null): string => {
  const fromFrequency = preferredPriceUnitFromFrequency(frequency, priceKeys);
  if (fromFrequency) return fromFrequency;
  return checkArray(priceKeys);
};
const calculateRating = (rating: number) => {
  const result = rating / 100 * 5;
  if (result > 5) {
    return 5;
  } else if (result < 1) {
    return 1;
  }
  return result;
};
const MapContainer: FC<{
  listing: MyListing;
  selectedList?: number | null;
  selectList?: (listId: number | null) => void;
  hoverListing: number | null;
  mapRef: google.maps.Map | null;
  type?: string;
  transformArray?: object[];
  siblings?: MyListing[];
  clusterCount?: number;
  /** Called right before the popup auto-pans the map to fit itself. */
  onAutoPan?: () => void;
}> = ({
  listing,
  selectedList = null,
  selectList,
  hoverListing,
  mapRef,
  type,
  transformArray,
  siblings,
  clusterCount,
  onAutoPan
}) => {
  const searchParams = useSearchParams();
  const frequency = searchParams.get('frequency');
  const url = `/${buildWorkspacePath({
    country: listing?.city?.country,
    city: listing?.city,
    workspaceSlug: listing?.workspace?.slug,
    listingSlug: type === 'workspace' ? undefined : listing.slug
  })}`;
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const [distance, setDistance] = useState(0);
  const [getdistance, setGetdistance] = useState(false);
  const [baseCurrencyName, setBaseCurrencyName] = useState('');
  const [price_unit, setPriceUnit] = useState(() => resolvePriceUnit(getObjectKeys(listing.rules?.price || []), null));
  const [price, setPrice] = useState('');
  const [listingRating, setListingRating] = useState(0);
  const rating_feature = useWebReviewsDisplay(type === 'workspace' ? 'workspace_explore' : 'listing_explore');
  const languageCode = useAppSelector((state: RootState) => state.listing.defaultLanguage);
  const t = useTranslations('explore');
  const [hoveredMarker, setHoveredMarker] = useState<number | null>(null);
  const [currentMarker, setActiveMarker] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const handleMouseEnter = (id: number) => setHoveredId(id);
  const handleMouseLeave = () => setHoveredId(null);
  useEffect(() => {
    setActiveMarker(selectedList);
  }, [selectedList]);
  const id = listing.id as number;
  const location = typeof listing.location === 'string' ? JSON.parse(listing.location) : listing.location;
  const lat = type == 'workspace' ? listing.workspace?.location.coordinates[1] as number : location?.coordinates?.[1] as number;
  const lng = type == 'workspace' ? listing.workspace?.location.coordinates[0] as number : location?.coordinates?.[0] as number;
  const getDistance = () => {
    const distanceService = new DistanceCalculatorService();
    distanceService.calculateDistanceFromCurrentLocation(lat as number, lng as number).then(({
      success,
      distance
    }) => {
      if (success) {
        setGetdistance(true);
        setDistance(distance);
      } else {
        setGetdistance(false);
      }
    });
  };
  useEffect(() => {
    getDistance();
    const price_key = getObjectKeys(listing.rules?.price || []);
    const key = resolvePriceUnit(price_key, frequency);
    setPriceUnit(key);
    if (listing?.metaData?.ratingBreakdown?.overall) {
      setListingRating(calculateRating(listing?.metaData?.ratingBreakdown?.overall));
    }
  }, [listing, frequency]);
  useEffect(() => {
    if (!!defaultCurrency?.code) {
      setBaseCurrencyName(defaultCurrency.name);
      const price = currencyConvert(currencyList, listing?.rules?.currency as string, defaultCurrency?.code, listing.rules?.price?.[price_unit]);
      const numberFormatService = new NumberFormatService();
      if (!!price) setPrice(numberFormatService.formatNumberWithCountingLabel(price, languageCode));
    }
  }, [defaultCurrency, listing, currencyList, price_unit, languageCode]);
  const handleMarkerClick = (markerId: number) => {
    // The map pans just enough to show the whole card once it renders
    // (see the fit effect below).
    if (!!selectList) {
      selectList(markerId);
    }
  };

  // Coordinates of a cluster member, matching the marker lat/lng logic.
  const memberCoord = (item: MyListing): {
    lat: number;
    lng: number;
  } | null => {
    const point = type === 'workspace' ? item.workspace?.location?.coordinates : (typeof item.location === 'string' ? JSON.parse(item.location) : item.location)?.coordinates;
    if (!point || point.length < 2) return null;
    return {
      lat: Number(point[1]),
      lng: Number(point[0])
    };
  };

  // The popup card is anchored *above* the marker and is fairly large, so a
  // marker near an edge would open clipped (OverlayView, unlike a native
  // InfoWindow, never auto-pans). Once the card has rendered we measure it
  // against the map viewport and pan the map just enough to show it whole —
  // like a native InfoWindow. The card's size isn't final on first paint (the
  // listings slider lays out and grows after mount, and can measure 0 wide),
  // so a ResizeObserver re-runs the fit whenever the card resizes.
  const popupRef = useRef<HTMLDivElement>(null);
  // State, not a ref: OverlayView mounts its children asynchronously (after
  // the overlay's onAdd), so the card doesn't exist yet when this component's
  // effects first run. A callback ref re-runs the fit effect once it does.
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);
  const isPopupOpen = currentMarker === listing.id;
  const onAutoPanRef = useRef(onAutoPan);
  onAutoPanRef.current = onAutoPan;
  useEffect(() => {
    if (!isPopupOpen || !mapRef || !cardEl) return;
    const map = mapRef;
    const el = cardEl;

    // panBy animates; measuring mid-animation would see the card only
    // partly moved and pan again on top (overshooting). Hold fits until
    // the map settles, then check once more.
    let panning = false;
    let idleListener: google.maps.MapsEventListener | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const settle = () => {
      idleListener?.remove();
      idleListener = null;
      clearTimeout(settleTimer);
      panning = false;
    };
    const fit = () => {
      if (panning) return;
      const mapDiv = map.getDiv();
      if (!mapDiv) return;
      const card = el.getBoundingClientRect();
      const box = mapDiv.getBoundingClientRect();
      if (card.width === 0 || card.height === 0) return;
      const margin = 12;
      // Keep clear of the +/- zoom buttons (top-right, 40px wide at
      // 12px) when the map is wide enough to spare the room.
      const controlsInset = 64;
      const rightInset = box.width >= card.width + margin + controlsInset ? controlsInset : margin;

      // Positive panBy x/y moves the view right/down, i.e. the card
      // left/up on screen.
      let dx = 0;
      if (card.width > box.width - margin - rightInset) {
        dx = card.left - (box.left + margin);
      } else if (card.left < box.left + margin) {
        dx = card.left - (box.left + margin);
      } else if (card.right > box.right - rightInset) {
        dx = card.right - (box.right - rightInset);
      }
      let dy = 0;
      if (card.height > box.height - 2 * margin || card.top < box.top + margin) {
        dy = card.top - (box.top + margin);
      } else if (card.bottom > box.bottom - margin) {
        dy = card.bottom - (box.bottom - margin);
      }
      dx = Math.round(dx);
      dy = Math.round(dy);
      if (dx === 0 && dy === 0) return;
      panning = true;
      idleListener = map.addListener('idle', () => {
        settle();
        fit();
      });
      // Fallback if no idle arrives (e.g. the pan was clamped at the
      // map's edge and nothing moved) so fits aren't blocked for good.
      settleTimer = setTimeout(settle, 1500);
      onAutoPanRef.current?.();
      map.panBy(dx, dy);
    };
    let raf2 = 0;
    // Double rAF: let Google's OverlayView draw/position the card before we
    // measure it.
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(fit);
    });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => fit()) : null;
    ro?.observe(el);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro?.disconnect();
      settle();
    };
  }, [isPopupOpen, mapRef, cardEl]);

  // Keep interactions inside the popup (slider arrows, favourite, links) from
  // leaking to the map. preventMapHitsAndGesturesFrom shields click/drag/wheel
  // but NOT dblclick, so two quick arrow clicks would still zoom the map — we
  // additionally stop dblclick (and the mousedowns that compose it) here.
  useEffect(() => {
    if (!isPopupOpen) return;
    const el = popupRef.current;
    if (!el) return;
    const OV = window.google?.maps?.OverlayView;
    if (OV?.preventMapHitsAndGesturesFrom) {
      OV.preventMapHitsAndGesturesFrom(el);
    }
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('dblclick', stop);
    el.addEventListener('mousedown', stop);
    return () => {
      el.removeEventListener('dblclick', stop);
      el.removeEventListener('mousedown', stop);
    };
  }, [isPopupOpen]);

  // Clicking a cluster: if its members sit at different places, zoom in a few
  // levels centred on it so it splits into smaller groups (repeat clicks
  // drill down). If they're stacked at one point — e.g. many listings in one
  // building — zooming can never separate them, so open the popup list
  // instead. Only ever zooms in, never out.
  const zoomToCluster = () => {
    const map = mapRef;
    if (!map) return;
    const coords = siblingListings.map(memberCoord).filter((c): c is {
      lat: number;
      lng: number;
    } => c != null);
    const spread = coords.length > 1 ? Math.max(Math.max(...coords.map(c => c.lat)) - Math.min(...coords.map(c => c.lat)), Math.max(...coords.map(c => c.lng)) - Math.min(...coords.map(c => c.lng))) : 0;

    // Effectively one point (~<50m) → cannot separate by zoom; show the
    // list. The measuring effect places the popup so it fits.
    if (spread < 0.0005) {
      if (selectList) selectList(id);
      return;
    }
    const current = map.getZoom() ?? 10;
    map.setZoom(Math.min(current + 3, 18));
    map.panTo({
      lat,
      lng
    });
  };
  const moveToRoute = () => {
    firebaseAnalytics('listing_clicked');
  };
  const [target, setTarget] = useState('_self');
  const [rel, setRel] = useState('');
  useEffect(() => {
    if (window.innerWidth > 992) {
      setTarget('_blank');
      setRel('noopener noreferrer');
    }
  }, []);
  const workspaceId = listing.workspace?.id as number;
  const listingCounter = clusterCount ?? (transformArray?.[workspaceId] as {
    counter?: number;
  })?.counter ?? 0;
  const siblingListings = siblings ?? (transformArray?.[workspaceId] as {
    listings?: MyListing[];
  })?.listings ?? [];
  const isHoverListingInCluster = hoverListing != null && (hoverListing === listing.id || siblingListings.some(member => member.id === hoverListing));
  const isCluster = listingCounter > 1;

  // A workspace marker opens the listings *inside* that workspace — the same
  // cards the map shows when the user filters by listings. Explore's
  // workspace mode is `distinct=workspace`, so they aren't in the page
  // payload; fetch them, but only once the popup is actually open.
  const isSingleWorkspace = type === 'workspace' && !isCluster;
  const {
    listings: workspaceListings
  } = useWorkspaceListings(listing.workspace?.id as number | undefined, isSingleWorkspace && isPopupOpen);
  // Until they land (or if the workspace has none/the request failed) the
  // workspace's own card stands in, so the popup is never empty.
  const showWorkspaceListings = isSingleWorkspace && workspaceListings.length > 0;
  const closePopup = () => {
    if (selectList) selectList(null);
  };
  // Provider-backed (IWG / Regus) sites get a recommended badge on the pill.
  const isRecommended = !!getListingProviderCode(listing);
  const currencyCode = defaultCurrency?.code || baseCurrencyName;
  const unitLabel = t(`mapUnit.${price_unit}` as never);
  return <Fragment>
            <OverlayView key={listing.id} position={{
      lat: lat,
      lng: lng
    }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({
      x: 0,
      y: 0
    })} data-sentry-element="OverlayView" data-sentry-source-file="MapContainer.tsx">
                <div onMouseEnter={() => handleMouseEnter(id)} onMouseLeave={handleMouseLeave} style={{
        zIndex: hoveredId === id || isHoverListingInCluster ? 999 : 1,
        position: "relative"
      }} className={styles['price-pill-container']}>
                    {/* Price pill — a cluster (count > 1) shows "from …" plus a
                        count badge; provider (IWG/Regus) sites get a recommended
                        icon before the price. Click still opens the images. */}
                    <div draggable="false" className={`${styles['price-pill']} ${isHoverListingInCluster || hoveredMarker === listing.id ? styles.active : ''}`} onClick={e => {
          e.stopPropagation();
          // A cluster zooms in to split apart; a single marker
          // opens its images popup.
          if (isCluster) {
            zoomToCluster();
          } else if (mapRef) {
            handleMarkerClick(id);
          }
          setHoveredMarker(id);
        }} onMouseOver={e => {
          e.stopPropagation();
          setHoveredMarker(id);
        }} onMouseOut={e => {
          e.stopPropagation();
          setHoveredMarker(null);
        }}>
                        {isRecommended && <Image src="/assets/icons/recommended-icon.svg" alt="" width={14} height={14} className={styles['pill-recommended']} />}
                        {isCluster && <span className={styles['pill-from']}>
                                {t('mapFrom')}
                            </span>}
                        <span className={styles['pill-amount']}>
                            {price ? `${price} ${currencyCode}` : currencyCode}
                        </span>
                        <span className={styles['pill-unit']}>
                            /{unitLabel}
                        </span>
                        {isCluster && <span className={styles['pill-count']}>
                                {listingCounter}
                            </span>}
                    </div>
                </div>
            </OverlayView>
            {currentMarker === listing.id && <OverlayView position={{
      lat: lat,
      lng: lng
    }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <>
                        <div ref={popupRef} style={{
          zIndex: 999,
          position: 'relative'
        }}>
                            <div ref={setCardEl} className={styles['info-window']}>
                                {isSingleWorkspace && (showWorkspaceListings ? <ListingSliderPopupClient languageCode={languageCode} listings={workspaceListings} type="similarListingResponsive" cardType="listing" onClose={closePopup} /> : <CardOnMap languageCode={languageCode} item={listing} distance={distance} getdistance={getdistance} dir_rtl={languageCode === 'ar'} baseCurrencyName={baseCurrencyName} price_unit={price_unit} price={price} listingRating={listingRating} rating_feature={rating_feature} moveToRoute={moveToRoute} url={url} target={target} rel={rel} type={type} onClose={closePopup} />)}
                                {(type === 'workspace' && isCluster || type === 'listing') && <ListingSliderPopupClient languageCode={languageCode} listings={siblingListings.length > 0 ? siblingListings : [listing]} type="similarListingResponsive" cardType={type === 'workspace' ? 'workspace' : 'listing'} onClose={closePopup} />}
                            </div>
                        </div>
                    </>
                </OverlayView>}
        </Fragment>;
};
export default MapContainer;